/**
 * SubmissionIssuesTab — top-level, submission-wide "Issues to review": every
 * gap / regulatory issue across the run as one flat card list, open first and
 * then in submission (section) order, each card carrying its section ref.
 *
 * The section Inspector already has a per-section Issues tab (DataGapsTabPanel);
 * this is its run-wide sibling: one place to see all open + resolved + dismissed
 * + stale issues at once, ordered the way the document reads. Rows reuse the
 * shared ``IssueRow`` component so the two views stay in sync — same kind
 * labels, regulatory "to resolve" guidance, and per-row actions (jump-to-marker
 * in Word, Mark N/A, Reopen, Resolve with asthra).
 *
 * DECISION (launch film): a flat list instead of collapsible per-section
 * groups. WHY: each card already shows its section ref, the groups started
 * collapsed (an extra click per section to see anything), and nothing else
 * depends on the grouping — bulk-resolve targets are still built per section
 * from the payload.
 *
 * Data: GET /runs/{runId}/issues?status=all (see ``getRunIssues``). The server
 * orders sections by ``scoped_module_ids`` and enriches regulatory rows; this
 * component flattens them into status bands (open first), keeping that
 * section order within each band.
 *
 * Fresh check on open: after the rollup loads, every section that still has an
 * OPEN issue has its live Word text SAVED (changed sections only, one Word scan,
 * one bulk write), and the rollup is re-read — so a flag whose marker the writer
 * deleted resolves the moment the tab opens, no Refresh button, and only once
 * that deletion is in the saved draft (a resolved flag can't come back on the
 * next Load). Skipped while this document doesn't own the run's text (auto-save
 * off) or mid-load; outside Word (web/guru) the server's own sweep settles
 * absent markers against the SSOT.
 */

import { makeStyles, Spinner } from "@fluentui/react-components";
import type React from "react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
	type FixOutcome,
	type FixPass,
	getResolveHistory,
	getRunAuditLibrary,
	getRunIssues,
	patchSectionIssue,
	type RunAuditLibrary,
	type RunIssuesResponse,
	type SectionIssue,
	startRunAuditLibrary,
} from "../../api/client";
import { openCleanCopy } from "../../services/cleanCopy";
import { isLiveDocLoading } from "../../services/liveDocLoadState";
import { isAutoSaveActiveFor } from "../../services/syncEngine";
import { enqueueWordWrite } from "../../services/wordWriteQueue";
import { navigateToSection, selectTextMarker } from "../../services/wordWriter";
import { getAuditLibraryBuild, getEntry, reviewKey, subscribeAuditLibraryBuild } from "../reviewStore";
import { AuditLibraryPanel } from "./inspector/AuditLibraryPanel";
import { InlineChange } from "./inspector/InlineChange";
import { IssueRow, isSelectableIssue, severityRank, STATUS_ORDER, toFixIssue } from "./inspector/IssueRow";
import { type FixIssue, issueTypes } from "../review/fixProtocol";
import { ProposalReviewPanel } from "./ProposalReviewPanel";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import { refusalMessage } from "../payments/paywall";
import type { ResolveIssueOutcome, ResolveSummary } from "../hooks/useResolveIssuesFlow";
import { type ProposalQueueItem, UNDO_MS, type UseProposalReviewFlowResult } from "../hooks/useProposalReviewFlow";
import type { AgentActivityMap } from "../services/agentActivityFeed";

/** A section + the specific open issues selected to bulk-resolve. Carries the
 *  section number/title for the wizard's review step (stripped before POST). */
export interface ResolveSelectionTarget {
	module_id: string;
	number: string;
	title: string;
	issue_ids: string[];
	/** Type and description of each issue, for the Fix window's list. */
	issues?: FixIssue[];
}

interface SubmissionIssuesTabProps {
	/** Active run id. Null before a run exists — the tab shows an empty hint. */
	runId: string | null;
	/** Host-wired "resolve with asthra" — seeds a chat turn scoped to the issue's
	 *  section with the marker text. Button hidden when absent. */
	onResolveWithAsthra?: (issue: SectionIssue) => void;
	/** Reports the current open-issue count up to the shell so the Issues-tab
	 *  badge stays live after an in-tab dismiss/reopen (no shell refetch). Fired
	 *  on load and after every successful patch. */
	onOpenCountChange?: (openCount: number) => void;
	/** Host-wired bulk resolve: opens the resolve wizard for the selected open
	 *  issues (grouped by section). Selection UI is hidden when absent. */
	onResolveSelected?: (targets: ResolveSelectionTarget[]) => void;
	/** External re-fetch trigger. The shell bumps it after a Save (the server
	 *  reconciled the ledger inside ``bulk-edit`` — writer-deleted markers →
	 *  resolved) and after an accepted chat edit, so the list reflects the new
	 *  state without the writer pressing Refresh. The initial mount fetch is
	 *  ``load``'s own effect; this one skips its first (mount) value. */
	issuesRefreshSignal?: number;
	/** Re-fetch trigger for the "Proposals to review" band — the shell bumps it
	 *  when a resolve pass produces a proposal / finishes. */
	proposalsRefreshSignal?: number;
	/** Bumped by the shell to open the review window on the pending changes. */
	reviewWindowSignal?: number;
	/** Active source library — scopes a Revise rewrite's retrieval. */
	libraryId?: string | null;
	/** Fired after a proposal is approved/rejected so the shell can refresh the
	 *  Issues list (an approve resolved the issue; a reject left it open). */
	onProposalApplied?: () => void;
	/** Counts from the last finished "Fix with Asthra" pass — the summary line
	 *  at the top. Hidden when null. */
	resolveSummary?: ResolveSummary | null;
	/** Hide the summary line. */
	onDismissResolveSummary?: () => void;
	/** Open the Fix window on the last pass's per-issue results. */
	onShowFixResults?: () => void;
	/** Sections of a running pass — their open rows read "Fixing…". */
	fixingModuleIds?: ReadonlySet<string>;
	/** Flags the pass was started on: only these read "Fixing…", and they sit
	 *  at the top of the list so the pass is followed here, not on the Spine. */
	fixingIssueIds?: ReadonlySet<string>;
	/** Live agent feed by section; a flag being fixed shows its newest line. */
	fixActivityBySection?: AgentActivityMap;
	/** This pass's per-flag verdicts (fresher than the fetched ``last_fix``). */
	liveIssueOutcomes?: Readonly<Record<string, ResolveIssueOutcome>>;
	/** Writes approved title-page fields into the open document. */
	writeTitleFields?: (fields: Record<string, string>) => Promise<unknown>;
	/** One click: fix these flags now from the whole library (no window). Given,
	 *  rows get a "Fix" action, "retry" re-runs directly and the bulk button
	 *  fixes straight away; ``onResolveSelected`` becomes "Choose sources…". */
	onFixNow?: (targets: ResolveSelectionTarget[]) => void;
	/** The shell's one review queue — the band and each row's inline change. */
	reviewFlow?: UseProposalReviewFlowResult;
}

/** One segment of the end-of-pass summary line; clicking it filters the list. */
export interface ResolveSummarySegment {
	outcome: FixOutcome;
	count: number;
	label: string;
}

/** Collapse bursts of refresh signals (approve, per-section gap-fill ends). */
const REFRESH_DEBOUNCE_MS = 400;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The last pass in three numbers: fixed (a change is ready), not fixed, and
 *  pending (stopped before it was reached). */
export function resolveSummaryTally(summary: ResolveSummary): { fixed: number; notFixed: number; pending: number } {
	return {
		fixed: summary.proposals,
		notFixed: summary.not_in_sources + summary.not_placed + summary.failed,
		pending: summary.stopped,
	};
}

/** The section picker's choices: every section with issues in the view, in
 *  document order, with its count. */
export function issueSectionOptions(
	sections: RunIssuesResponse["sections"],
	countIn: (issue: SectionIssue) => boolean,
): { moduleId: string; label: string; count: number }[] {
	return sections
		.map((s) => ({
			moduleId: s.module_id,
			label: [sectionRef(s.number), s.title].filter(Boolean).join(" "),
			count: s.issues.filter(countIn).length,
		}))
		.filter((o) => o.count > 0);
}

/** "Last fix: 0 fixed · 5 not fixed · 0 pending". */
export function lastFixLine(summary: ResolveSummary): string {
	const t = resolveSummaryTally(summary);
	return `Last fix: ${t.fixed} fixed · ${t.notFixed} not fixed · ${t.pending} pending`;
}

/** The summary line's segments, in reading order, skipping zero counts. */
export function resolveSummarySegments(summary: ResolveSummary): ResolveSummarySegment[] {
	const segs: ResolveSummarySegment[] = [
		{ outcome: "proposed", count: summary.proposals, label: `${plural(summary.proposals, "change", "changes")} ready to review` },
		{ outcome: "not_in_sources", count: summary.not_in_sources, label: `${plural(summary.not_in_sources, "flag", "flags")} not in your sources` },
		{ outcome: "rewrite_rejected", count: summary.rewrite_rejected, label: `${plural(summary.rewrite_rejected, "change", "changes")} didn't fill the flag` },
		{ outcome: "has_pending", count: summary.has_pending, label: `${plural(summary.has_pending, "flag", "flags")} already waiting for review` },
		{ outcome: "busy", count: summary.busy, label: `${plural(summary.busy, "flag", "flags")} already being worked on` },
		{ outcome: "not_placed", count: summary.not_placed, label: `${plural(summary.not_placed, "flag", "flags")} not found in the saved text` },
		{ outcome: "failed", count: summary.failed, label: `${summary.failed} failed` },
		{ outcome: "stopped", count: summary.stopped, label: `${summary.stopped} stopped` },
	];
	return segs.filter((s) => s.count > 0);
}

/** The Issues tab's views: what is open, what a fix was tried on, what is
 *  done, everything, and the history of fix passes. */
export type IssuesView = "open" | "tried" | "resolved" | "all" | "history";

/** Whether an issue belongs in a view (``history`` lists passes, not issues). */
export function inIssuesView(
	issue: SectionIssue,
	view: IssuesView,
	live?: Readonly<Record<string, ResolveIssueOutcome>>,
): boolean {
	if (view === "open") return issue.status === "open";
	if (view === "tried") return Boolean(issue.last_fix || live?.[issue.id]);
	if (view === "resolved") return issue.status === "resolved" || issue.status === "dismissed";
	return view === "all";
}

/** One fix pass as the History view shows it. */
export interface FixPassRecord {
	passId: string;
	at: string | null;
	tried: number;
	outcomes: Partial<Record<FixOutcome, number>>;
	/** Of the issues tried, how many are resolved or N/A now. */
	resolvedNow: number;
	/** Who started the pass and what it searched (from the server's record). */
	by?: string | null;
	scope?: string[];
}

/** Passes from the server's record (every pass, even of an issue tried twice),
 *  then any pass only the issues' ``last_fix`` still remembers (one that ran
 *  before the record was kept). Newest first. */
export function mergeFixHistory(passes: FixPass[], issues: SectionIssue[]): FixPassRecord[] {
	const statusById = new Map(issues.map((i) => [i.id, i.status]));
	const fromServer: FixPassRecord[] = passes.map((p) => {
		const outcomes: Partial<Record<FixOutcome, number>> = {};
		let resolvedNow = 0;
		for (const f of p.flags ?? []) {
			outcomes[f.outcome] = (outcomes[f.outcome] ?? 0) + 1;
			const st = statusById.get(f.issue_id);
			if (st === "resolved" || st === "dismissed") resolvedNow += 1;
		}
		return {
			passId: p.pass_id,
			at: p.finished_at ?? p.started_at,
			tried: (p.flags ?? []).length,
			outcomes,
			resolvedNow,
			by: p.requested_by,
			scope: p.document_scope ?? [],
		};
	});
	const known = new Set(fromServer.map((r) => r.passId));
	const older = fixHistory(issues).filter((r) => !known.has(r.passId));
	return [...fromServer, ...older].sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
}

/** Fix passes, newest first, from each issue's latest attempt. An issue tried
 *  twice counts under its latest pass only. */
export function fixHistory(issues: SectionIssue[]): FixPassRecord[] {
	const byPass = new Map<string, FixPassRecord>();
	for (const issue of issues) {
		const fix = issue.last_fix;
		if (!fix) continue;
		const passId = fix.pass_id ?? "earlier";
		const rec = byPass.get(passId) ?? { passId, at: fix.at ?? null, tried: 0, outcomes: {}, resolvedNow: 0 };
		rec.tried += 1;
		rec.outcomes[fix.outcome] = (rec.outcomes[fix.outcome] ?? 0) + 1;
		if (issue.status === "resolved" || issue.status === "dismissed") rec.resolvedNow += 1;
		if (fix.at && (!rec.at || fix.at < rec.at)) rec.at = fix.at;
		byPass.set(passId, rec);
	}
	return [...byPass.values()].sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
}

const OUTCOME_WORDS: Record<FixOutcome, string> = {
	proposed: "change proposed",
	not_in_sources: "not in your sources",
	rewrite_rejected: "change didn't fill it",
	not_placed: "couldn't be placed",
	has_pending: "already waiting for review",
	busy: "already being worked on",
	failed: "failed",
	stopped: "stopped",
	rejected: "rejected",
};

/** "5 tried · 2 change proposed · 3 not in your sources · 1 resolved now". */
export function fixPassLine(rec: FixPassRecord): string {
	const parts = [`${rec.tried} tried`];
	for (const k of Object.keys(OUTCOME_WORDS) as FixOutcome[]) {
		const n = rec.outcomes[k];
		if (n) parts.push(`${n} ${OUTCOME_WORDS[k]}`);
	}
	parts.push(`${rec.resolvedNow} resolved now`);
	return parts.join(" · ");
}

/** The verdict that counts for an issue right now: this pass's live one, else
 *  its stored ``last_fix`` — ignored when it belongs to an older pass than the
 *  summary on screen. */
export function effectiveOutcome(
	issue: SectionIssue,
	live: Readonly<Record<string, ResolveIssueOutcome>> | undefined,
	passId: string | null | undefined,
): ResolveIssueOutcome | null {
	const fromLive = live?.[issue.id];
	if (fromLive) return fromLive;
	const fix = issue.last_fix;
	if (!fix) return null;
	if (passId && fix.pass_id && fix.pass_id !== passId) return null;
	return fix;
}

const useStyles = makeStyles({
	root: {
		height: "100%",
		overflowY: "auto",
		padding: "10px 12px 24px",
		boxSizing: "border-box",
	},
	loading: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
	},
	error: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: statusColors.danger,
		margin: "4px 0",
	},
	empty: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
		fontStyle: "italic",
		padding: "8px 2px",
	},
	// Sticky summary bar of status tallies.
	summary: {
		position: "sticky",
		top: 0,
		zIndex: 1,
		display: "flex",
		flexDirection: "column",
		gap: "6px",
		background: surfaceColors.paper,
		borderBottom: `1px solid ${surfaceColors.line}`,
		padding: "6px 2px 10px",
		marginBottom: "10px",
	},
	title: {
		fontFamily: fonts.body,
		fontSize: "15px",
		fontWeight: 600,
		color: inkColors.ink,
		margin: 0,
	},
	titleRow: {
		display: "flex",
		alignItems: "baseline",
		justifyContent: "space-between",
		gap: "10px",
	},
	cleanCopyBtn: {
		border: "none",
		background: "transparent",
		padding: 0,
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink3,
		textDecoration: "underline",
		cursor: "pointer",
		":disabled": { cursor: "default", opacity: 0.6 },
	},
	cleanCopyNote: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink3,
	},
	summaryRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "10px",
		flexWrap: "wrap",
	},
	counts: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
		flexWrap: "wrap",
		fontFamily: fonts.mono,
		fontSize: "10px",
		letterSpacing: "0.6px",
		textTransform: "uppercase",
		color: inkColors.ink4,
	},
	countItem: { display: "inline-flex", alignItems: "center", gap: "4px" },
	viewTabs: {
		display: "flex",
		gap: "2px",
		flexWrap: "wrap",
		borderBottom: `1px solid ${surfaceColors.line}`,
		margin: "6px 0 8px",
	},
	viewTab: {
		border: "none",
		borderBottom: "2px solid transparent",
		background: "transparent",
		padding: "4px 8px",
		fontFamily: fonts.body,
		fontSize: "11.5px",
		color: inkColors.ink3,
		cursor: "pointer",
	},
	viewTabOn: { color: inkColors.ink, borderBottom: `2px solid ${brandColors.primary}`, fontWeight: 600 },
	sectionPick: { display: "flex", alignItems: "center", gap: "8px", margin: "0 0 8px" },
	sectionSelect: {
		flex: "1 1 auto",
		minWidth: 0,
		height: "28px",
		border: `1px solid ${surfaceColors.line}`,
		borderRadius: "4px",
		background: "var(--surface-raised)",
		fontFamily: fonts.body,
		fontSize: "12px",
		color: inkColors.ink,
	},
	sectionHead: {
		fontFamily: fonts.mono,
		fontSize: "11px",
		textTransform: "uppercase",
		letterSpacing: "0.04em",
		color: inkColors.ink3,
		padding: "6px 2px 0",
	},
	historyRow: {
		display: "flex",
		flexDirection: "column",
		gap: "2px",
		padding: "8px 10px",
		border: `1px solid ${surfaceColors.line}`,
		borderRadius: "6px",
		fontFamily: fonts.body,
		fontSize: "12px",
		color: inkColors.ink2,
	},
	historyWhen: { fontFamily: fonts.mono, fontSize: "10.5px", color: inkColors.ink4 },
	countNum: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
	countOpen: { color: statusColors.danger },
	list: { display: "flex", flexDirection: "column", gap: "8px" },
	// End-of-pass summary line (from resolve_summary).
	passSummary: {
		display: "flex",
		alignItems: "flex-start",
		gap: "8px",
		border: `1px solid ${surfaceColors.line}`,
		borderRadius: "8px",
		background: "var(--surface-raised)",
		padding: "8px 10px",
		marginBottom: "10px",
		fontFamily: fonts.body,
		fontSize: "11.5px",
		color: inkColors.ink2,
		lineHeight: 1.5,
	},
	passSummaryText: { flex: "1 1 auto", minWidth: 0 },
	passSummaryHead: { fontWeight: 600, color: inkColors.ink, marginRight: "6px" },
	passSeg: {
		border: "none",
		background: "transparent",
		padding: 0,
		fontFamily: fonts.body,
		fontSize: "11.5px",
		color: brandColors.primary,
		cursor: "pointer",
		textDecorationLine: "underline",
		textDecorationStyle: "dotted",
		":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px" },
	},
	passSegActive: { fontWeight: 700, textDecorationStyle: "solid" },
	passSep: { color: inkColors.ink4, margin: "0 5px" },
	passDismiss: {
		border: "none",
		background: "transparent",
		padding: "0 2px",
		color: inkColors.ink4,
		cursor: "pointer",
		fontSize: "13px",
		lineHeight: 1,
	},
	filterNote: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink3,
		marginBottom: "8px",
	},
	// Bulk-resolve selection toolbar (shown when onResolveSelected is wired).
	selectAll: {
		display: "inline-flex",
		alignItems: "center",
		gap: "5px",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink2,
		cursor: "pointer",
	},
	checkbox: { cursor: "pointer", accentColor: brandColors.primary },
	selCount: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink3 },
	typeRow: { display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap", marginTop: "6px" },
	typeLead: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink3, marginRight: "2px" },
	typeChip: {
		border: `1px solid ${surfaceColors.lineStrong}`,
		borderRadius: "11px",
		background: "var(--surface-raised)",
		color: inkColors.ink2,
		fontFamily: fonts.body,
		fontSize: "11px",
		padding: "2px 9px",
		cursor: "pointer",
	},
	typeChipOn: { border: `1px solid ${brandColors.primary}`, background: brandColors.light, color: inkColors.ink },
	undoRow: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontFamily: fonts.body,
		fontSize: "11.5px",
		color: inkColors.ink2,
		margin: "4px 0",
	},
	sourcesLink: {
		marginLeft: "auto",
		border: "none",
		background: "none",
		padding: 0,
		fontFamily: fonts.body,
		fontSize: "11px",
		fontWeight: 600,
		color: inkColors.ink3,
		textDecorationLine: "underline",
		cursor: "pointer",
		":disabled": { opacity: 0.5, cursor: "default" },
	},
	resolveBtn: {
		marginLeft: "auto",
		border: "none",
		borderRadius: "6px",
		background: brandColors.primary,
		color: "var(--on-brand)",
		fontFamily: fonts.body,
		fontSize: "11px",
		fontWeight: 600,
		padding: "5px 12px",
		cursor: "pointer",
		":hover": { opacity: 0.9 },
		":disabled": { background: surfaceColors.lineStrong, cursor: "default", opacity: 1 },
	},
});

/** Re-tally status counts from the (possibly patched) sections. */
function tallyStatusCounts(sections: RunIssuesResponse["sections"]): RunIssuesResponse["status_counts"] {
	const counts = { open: 0, resolved: 0, dismissed: 0, stale: 0, total: 0 };
	for (const section of sections) {
		for (const issue of section.issues) {
			counts.total += 1;
			counts[issue.status] += 1;
		}
	}
	return counts;
}

/** One flat-list entry: an issue plus the section it belongs to. */
export interface FlatIssue {
	issue: SectionIssue;
	sectionNumber: string;
}

/** Flatten the per-section rollup into one list: status bands (open first),
 *  then the server's section order, then severity, then first seen. Pure. */
export function flattenIssues(sections: RunIssuesResponse["sections"]): FlatIssue[] {
	const rows = sections.flatMap((s, sectionIdx) =>
		s.issues.map((issue) => ({ issue, sectionNumber: s.number, sectionIdx })),
	);
	rows.sort(
		(a, b) =>
			STATUS_ORDER[a.issue.status] - STATUS_ORDER[b.issue.status] ||
			a.sectionIdx - b.sectionIdx ||
			severityRank(a.issue.severity) - severityRank(b.issue.severity) ||
			(a.issue.first_seen_at ?? "").localeCompare(b.issue.first_seen_at ?? ""),
	);
	return rows.map(({ issue, sectionNumber }) => ({ issue, sectionNumber }));
}

/** "§9.1.1" from a section number; empty when the section has none. */
export function sectionRef(number: string | null | undefined): string {
	const n = (number ?? "").trim();
	return n ? `§${n}` : "";
}

/**
 * Fresh check against the live Word document: SAVE every section that still has
 * an OPEN issue (one Word scan, one bulk write, changed sections only) so the
 * server settles the ledger from the body it now holds. A marker the writer
 * deleted resolves only once that deletion is saved — so a later Load can never
 * bring back a flag the panel showed as resolved. Returns true when anything was
 * saved (the caller then re-reads the ledger).
 *
 * Skipped while this document doesn't own the run's text (auto-save off: a draft
 * still running, a placeholder body, the doc mid-load), and for any section
 * whose regulatory audit is running — its live doc is intentionally stripped of
 * [REGULATOR FLAG:…] markers until the audit re-projects them (reviewStore
 * ``startAudit``), and saving it then would delete every fresh flag.
 */
/** Runs whose open sections this pane already saved from the live doc. */
const sweptRunIds = new Set<string>();

/** Test hook — forget which runs were swept. */
export function __resetLiveDocSweepForTests(): void {
	sweptRunIds.clear();
}

async function saveOpenSectionsFromLiveDoc(runId: string, resp: RunIssuesResponse): Promise<boolean> {
	if (isLiveDocLoading() || !isAutoSaveActiveFor(runId)) return false;
	// Once per run: the tab remounts on every switch back to Issues (the Fix
	// window closing switches to it), and each remount re-read and re-saved
	// every flagged section. Auto-save is on here, so later edits are saved by
	// the watcher anyway.
	if (sweptRunIds.has(runId)) return false;
	const moduleIds = resp.sections
		.filter((s) => s.issues.some((i) => i.status === "open"))
		.filter((s) => getEntry(reviewKey(runId, s.module_id)).runState !== "running")
		.map((s) => s.module_id);
	if (moduleIds.length === 0) return false;
	sweptRunIds.add(runId);
	try {
		const { captureAndSaveModules } = await import("../services/writeBackBridge");
		const result = await captureAndSaveModules(runId, moduleIds, { enqueueWordWrite });
		return result.saved > 0;
	} catch {
		return false; // not in Word / save failed — keep the server rollup
	}
}

export const SubmissionIssuesTab: React.FC<SubmissionIssuesTabProps> = ({
	runId,
	onResolveWithAsthra,
	onOpenCountChange,
	onResolveSelected,
	issuesRefreshSignal,
	proposalsRefreshSignal,
	reviewWindowSignal,
	libraryId,
	onProposalApplied,
	resolveSummary,
	onDismissResolveSummary,
	onShowFixResults,
	fixingModuleIds,
	fixingIssueIds,
	fixActivityBySection,
	liveIssueOutcomes,
	writeTitleFields,
	onFixNow,
	reviewFlow,
}) => {
	const styles = useStyles();
	// Pending review changes by the issue they fill (from the review band), the
	// card to bring forward, and the summary-line filter.
	const [pendingByIssue, setPendingByIssue] = useState<Record<string, string>>({});
	const [focusRequest, setFocusRequest] = useState<{ proposalId: string; nonce: number } | null>(null);
	const [outcomeFilter, setOutcomeFilter] = useState<FixOutcome | null>(null);
	const [view, setView] = useState<IssuesView>("open");
	// A copy without review notes, to share: opened as a new Word document.
	const [cleanCopy, setCleanCopy] = useState<"idle" | "working">("idle");
	const [cleanCopyNote, setCleanCopyNote] = useState<string | null>(null);
	const makeCleanCopy = useCallback(async () => {
		if (!runId) return;
		setCleanCopy("working");
		setCleanCopyNote(null);
		try {
			const how = await openCleanCopy(runId);
			setCleanCopyNote(
				how === "opened"
					? "The clean copy opened in a new window. Save it there."
					: "The clean copy was downloaded.",
			);
		} catch {
			setCleanCopyNote("Couldn't make a clean copy. Try again.");
		} finally {
			setCleanCopy("idle");
		}
	}, [runId]);
	// One section (module id), or the whole document when null.
	const [sectionFilter, setSectionFilter] = useState<string | null>(null);
	const inSection = useCallback(
		(issue: { module_id: string }) => sectionFilter === null || issue.module_id === sectionFilter,
		[sectionFilter],
	);
	// Every fix pass the server recorded; re-read when a pass ends.
	const [serverPasses, setServerPasses] = useState<FixPass[]>([]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-read on each new pass summary
	useEffect(() => {
		if (!runId) return;
		let live = true;
		getResolveHistory(runId)
			.then((r) => {
				if (live) setServerPasses(r.passes ?? []);
			})
			.catch(() => {
				// Older server without the record: the issues' last attempts still show.
			});
		return () => {
			live = false;
		};
	}, [runId, resolveSummary]);
	// A new pass's summary starts unfiltered.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on each new summary
	useEffect(() => setOutcomeFilter(null), [resolveSummary]);
	const reviewChange = useCallback(
		(proposalId: string) => setFocusRequest((prev) => ({ proposalId, nonce: (prev?.nonce ?? 0) + 1 })),
		[],
	);
	const [payload, setPayload] = useState<RunIssuesResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	// Bulk-resolve selection — a flat set of open issue ids (globally unique).
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	// Accordion: at most one issue card open — opening one collapses the last.
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const toggleExpanded = useCallback(
		(issue: SectionIssue) => setExpandedId((prev) => (prev === issue.id ? null : issue.id)),
		[],
	);
	// The run's dynamic audit library (precedent-aware audit) — the provenance
	// behind the run's regulatory findings, shown atop the run-wide issues. Null
	// until fetched; the panel self-hides when the feature is off or the run has
	// none, so it is safe to render unconditionally.
	const [auditLib, setAuditLib] = useState<RunAuditLibrary | null>(null);
	const requestKeyRef = useRef<string>("");

	const loadAuditLibrary = useCallback(() => {
		if (!runId) {
			setAuditLib(null);
			return;
		}
		getRunAuditLibrary(runId)
			.then(setAuditLib)
			.catch(() => setAuditLib(null));
	}, [runId]);

	// Live audit-library build phase (SSE-driven) — narrates the build between the
	// 6s status polls and surfaces the custom-submission temp-library bootstrap the
	// poll can miss entirely (no documents yet). See DataGapsTabPanel for the twin.
	const auditBuildPhase = useSyncExternalStore(subscribeAuditLibraryBuild, () =>
		runId ? getAuditLibraryBuild(runId) : null,
	);
	const liveBuildMessage = auditBuildPhase?.message ?? null;

	// Manual (re)build of the run's audit library, with an optional steering note.
	// ``settingUpAudit`` is a latch held for the WHOLE (re)build (not just the POST),
	// released only once a poll sees the build reach a NEW terminal state — see the
	// matching note in DataGapsTabPanel. Clearing it at the 202 let polling stop
	// while the backend kept building, so the panel never advanced.
	const [settingUpAudit, setSettingUpAudit] = useState(false);
	const [auditBuildSeen, setAuditBuildSeen] = useState(false);
	const [auditSetupError, setAuditSetupError] = useState<string | null>(null);
	const rebuildTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearRebuildTimer = useCallback(() => {
		if (rebuildTimer.current) {
			clearTimeout(rebuildTimer.current);
			rebuildTimer.current = null;
		}
	}, []);
	const handleSetupAuditLibrary = useCallback(
		(steering: string) => {
			if (!runId) return;
			setAuditSetupError(null);
			setAuditBuildSeen(false);
			setSettingUpAudit(true);
			clearRebuildTimer();
			rebuildTimer.current = setTimeout(() => setSettingUpAudit(false), 5 * 60 * 1000);
			void startRunAuditLibrary(runId, steering)
				.then(() => loadAuditLibrary())
				.catch((err) => {
					setAuditSetupError(refusalMessage(err, () => handleSetupAuditLibrary(steering)));
					setSettingUpAudit(false);
					clearRebuildTimer();
				});
		},
		[runId, loadAuditLibrary, clearRebuildTimer],
	);

	// Poll while a build is in flight (or the rebuild latch is held) so the panel
	// advances to ready on its own.
	const auditStatus = auditLib?.audit_library?.status;
	const auditPolling =
		settingUpAudit || auditStatus === "acquiring" || auditStatus === "processing" || !!auditBuildPhase;
	useEffect(() => {
		if (!auditPolling) return;
		const t = setInterval(loadAuditLibrary, 6000);
		return () => clearInterval(t);
	}, [auditPolling, loadAuditLibrary]);

	// Release the latch once the build has started and then reached a terminal state.
	useEffect(() => {
		if (!settingUpAudit) return;
		if (auditStatus === "acquiring" || auditStatus === "processing") {
			if (!auditBuildSeen) setAuditBuildSeen(true);
			return;
		}
		if (auditBuildSeen && (auditStatus === "ready" || auditStatus === "empty" || auditStatus === "failed")) {
			setSettingUpAudit(false);
			clearRebuildTimer();
		}
	}, [settingUpAudit, auditBuildSeen, auditStatus, clearRebuildTimer]);

	useEffect(() => clearRebuildTimer, [clearRebuildTimer]);

	// ``refresh``: a signal-driven re-read (approve, save, audit, gap fill). It
	// keeps the list on screen and the writer's selection (minus flags that are no
	// longer open), and skips the live-doc save — the change that triggered it
	// already reached the server. Only the first load per run shows the spinner,
	// clears the selection and saves the live text first.
	const load = useCallback(
		async ({ refresh = false }: { refresh?: boolean } = {}) => {
			if (!runId) {
				setLoading(false);
				setPayload(null);
				setError(null);
				return;
			}
			const key = runId;
			requestKeyRef.current = key;
			if (!refresh) {
				setLoading(true);
				setPayload(null);
				setSelectedIds(new Set());
			}
			setError(null);
			setActionError(null);
			try {
				const resp = await getRunIssues(runId, "all");
				if (requestKeyRef.current !== key) return;
				// Fresh check on open: save each still-open section's live text, then
				// re-read the ledger the server settled from it. (Render flattens
				// it into status bands — see ``flattenIssues``.)
				const saved = refresh ? false : await saveOpenSectionsFromLiveDoc(runId, resp);
				if (requestKeyRef.current !== key) return;
				const fresh = saved ? await getRunIssues(runId, "all") : resp;
				if (requestKeyRef.current !== key) return;
				setPayload(fresh);
				if (refresh) {
					const open = new Set(
						fresh.sections.flatMap((sec) => sec.issues.filter(isSelectableIssue).map((i) => i.id)),
					);
					setSelectedIds((prev) => {
						const next = new Set([...prev].filter((id) => open.has(id)));
						return next.size === prev.size ? prev : next;
					});
				}
			} catch (err) {
				if (requestKeyRef.current !== key) return;
				const msg = err instanceof Error ? err.message : String(err);
				// A failed background refresh keeps the list; only the first load blanks.
				if (refresh) setActionError(`Couldn't refresh the issues: ${msg}`);
				else setError(msg);
			} finally {
				if (requestKeyRef.current === key) setLoading(false);
			}
		},
		[runId],
	);

	useEffect(() => {
		void load();
	}, [load]);

	// Re-fetch when the shell signals a ledger change (post-Save / accepted edit).
	// The mount value is skipped — the effect above already fetched — so a fresh
	// mount never double-fetches. Same guard pattern as DraftsPanel/HomeScene.
	// Debounced: one approve or a burst of per-section gap-fill ends bumps the
	// signal several times; they collapse into one quiet re-read.
	const refreshSignalSeenRef = useRef(false);
	// biome-ignore lint/correctness/useExhaustiveDependencies: issuesRefreshSignal is the external re-fetch trigger
	useEffect(() => {
		if (!refreshSignalSeenRef.current) {
			refreshSignalSeenRef.current = true;
			return;
		}
		const t = setTimeout(() => void load({ refresh: true }), REFRESH_DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [issuesRefreshSignal, load]);

	// Fetch the audit library on mount / run change, and re-fetch on the same
	// ledger-change signal the issues list uses (a run that just finished may have
	// only now finished acquiring precedent). Self-hiding, so no gating needed.
	// biome-ignore lint/correctness/useExhaustiveDependencies: issuesRefreshSignal is the re-fetch trigger
	useEffect(() => {
		loadAuditLibrary();
	}, [loadAuditLibrary, issuesRefreshSignal]);

	// Report the open count up to the shell whenever the payload changes (load or
	// in-tab patch) so the Issues-tab badge stays live without a shell refetch.
	useEffect(() => {
		if (payload) onOpenCountChange?.(payload.status_counts.open);
	}, [payload, onOpenCountChange]);

	// Jump to the marker in the live Word document; fall back to the section
	// heading when the exact marker text isn't found. Mirrors DataGapsTabPanel.
	const jumpToMarker = useCallback((issue: SectionIssue, sectionNumber: string) => {
		void selectTextMarker(issue.marker_text)
			.then((found) => {
				if (!found && sectionNumber) return navigateToSection(sectionNumber);
				return undefined;
			})
			.catch(() => {});
	}, []);

	// "Marked N/A · Undo" — for a few seconds after a dismiss.
	const [undoDismiss, setUndoDismiss] = useState<SectionIssue | null>(null);
	useEffect(() => {
		if (!undoDismiss) return;
		const t = setTimeout(() => setUndoDismiss(null), UNDO_MS);
		return () => clearTimeout(t);
	}, [undoDismiss]);
	const handlePatch = useCallback(
		async (issue: SectionIssue, status: "dismissed" | "open") => {
			setActionError(null);
			try {
				const updated = await patchSectionIssue(runId ?? "", issue.id, status);
				setUndoDismiss(status === "dismissed" ? updated : null);
				// A dismissed/reopened issue that is no longer open leaves the
				// selectable set — drop it from the bulk-resolve selection.
				if (updated.status !== "open") {
					setSelectedIds((prev) => {
						if (!prev.has(updated.id)) return prev;
						const next = new Set(prev);
						next.delete(updated.id);
						return next;
					});
				}
				setPayload((prev) => {
					if (!prev) return prev;
					const sections = prev.sections.map((s) =>
						s.module_id === updated.module_id
							? {
									...s,
									issues: s.issues.map((i) => (i.id === updated.id ? updated : i)),
									open_count: s.issues.reduce(
										(n, i) => n + ((i.id === updated.id ? updated.status : i.status) === "open" ? 1 : 0),
										0,
									),
								}
							: s,
					);
					return { ...prev, sections, status_counts: tallyStatusCounts(sections) };
				});
			} catch (err) {
				setActionError(err instanceof Error ? err.message : String(err));
			}
		},
		[runId],
	);

	// ── Bulk-resolve selection ────────────────────────────────────────────
	const selectionEnabled = Boolean(onResolveSelected);
	const allOpenIds = (payload?.sections ?? []).flatMap((s) =>
		s.issues.filter((i) => isSelectableIssue(i) && inSection(i)).map((i) => i.id),
	);
	const selectedCount = allOpenIds.filter((id) => selectedIds.has(id)).length;
	const allSelected = allOpenIds.length > 0 && selectedCount === allOpenIds.length;

	const toggleIssue = useCallback((issue: SectionIssue) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(issue.id)) next.delete(issue.id);
			else next.add(issue.id);
			return next;
		});
	}, []);

	// "Fix every flag of this type": one chip per type among the open flags.
	const openTypes = useMemo(
		() =>
			issueTypes(
				(payload?.sections ?? []).map((sec) => ({
					module_id: sec.module_id,
					number: sec.number,
					title: sec.title,
					issues: sec.issues.filter((i) => isSelectableIssue(i) && inSection(i)).map(toFixIssue),
				})),
			),
		[payload, inSection],
	);
	const toggleType = useCallback((ids: string[]) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			const all = ids.every((id) => next.has(id));
			for (const id of ids) {
				if (all) next.delete(id);
				else next.add(id);
			}
			return next;
		});
	}, []);

	const toggleAll = useCallback(() => {
		setSelectedIds((prev) => (allOpenIds.every((id) => prev.has(id)) ? new Set() : new Set(allOpenIds)));
	}, [allOpenIds]);

	const startResolve = useCallback(() => {
		if (!onResolveSelected || !payload) return;
		const targets: ResolveSelectionTarget[] = payload.sections
			.map((s) => ({
				module_id: s.module_id,
				number: s.number,
				title: s.title,
				issue_ids: s.issues.filter((i) => isSelectableIssue(i) && selectedIds.has(i.id)).map((i) => i.id),
				issues: s.issues.filter((i) => isSelectableIssue(i) && selectedIds.has(i.id)).map(toFixIssue),
			}))
			.filter((t) => t.issue_ids.length > 0);
		if (targets.length > 0) onResolveSelected(targets);
	}, [onResolveSelected, payload, selectedIds]);

	const issuesById = useMemo(() => {
		const map: Record<string, SectionIssue> = {};
		for (const sec of payload?.sections ?? []) for (const i of sec.issues) map[i.id] = i;
		return map;
	}, [payload]);

	// "Fix" / "retry" on a row: fix just that flag — straight away when the host
	// can, else through the Fix window.
	const retryFix = useCallback(
		(issue: SectionIssue) => {
			const run = onFixNow ?? onResolveSelected;
			if (!run) return;
			const sec = payload?.sections.find((s) => s.module_id === issue.module_id);
			run([
				{
					module_id: issue.module_id,
					number: sec?.number ?? "",
					title: sec?.title ?? "",
					issue_ids: [issue.id],
					issues: [toFixIssue(issue)],
				},
			]);
		},
		[onFixNow, onResolveSelected, payload],
	);
	// The bulk button fixes straight away; "Choose sources…" opens the window.
	const fixSelectedNow = useCallback(() => {
		if (!payload) return;
		const targets: ResolveSelectionTarget[] = payload.sections
			.map((s) => ({
				module_id: s.module_id,
				number: s.number,
				title: s.title,
				issue_ids: s.issues.filter((i) => isSelectableIssue(i) && selectedIds.has(i.id)).map((i) => i.id),
				issues: s.issues.filter((i) => isSelectableIssue(i) && selectedIds.has(i.id)).map(toFixIssue),
			}))
			.filter((t) => t.issue_ids.length > 0);
		if (targets.length > 0) onFixNow?.(targets);
	}, [onFixNow, payload, selectedIds]);
	// Each row's pending change, from the shared queue (inline Approve / Reject).
	const queuedById = useMemo(() => {
		const map: Record<string, ProposalQueueItem> = {};
		for (const item of reviewFlow?.queue ?? []) map[item.proposal.id] = item;
		return map;
	}, [reviewFlow?.queue]);
	const reviewBusy = reviewFlow ? reviewFlow.phase === "applying" || reviewFlow.phase === "revising" : false;

	// The review queue band — shown above the issues whenever a resolve pass has
	// produced pending proposals. Rendered in every runId branch so the writer can
	// review proposals even while the issues list is loading or errored.
	const reviewPanel = runId ? (
		<ProposalReviewPanel
			flow={reviewFlow}
			runId={runId}
			refreshSignal={proposalsRefreshSignal}
			libraryId={libraryId}
			onDecided={() => {
				// One re-read: the shell's signal reaches the effect above (debounced).
				if (onProposalApplied) onProposalApplied();
				else void load({ refresh: true });
			}}
			onError={setActionError}
			issuesById={issuesById}
			writeTitleFields={writeTitleFields}
			focusRequest={focusRequest}
			onPendingChange={setPendingByIssue}
			openWindowSignal={reviewWindowSignal}
		/>
	) : null;

	// End-of-pass summary: one line from resolve_summary; each count filters.
	const summarySegments = resolveSummary ? resolveSummarySegments(resolveSummary) : [];
	const summaryLine = resolveSummary ? (
		<div className={styles.passSummary} aria-live="polite" data-testid="resolve-summary">
			<div className={styles.passSummaryText}>
				<div className={styles.passSummaryHead}>{lastFixLine(resolveSummary)}</div>
				{summarySegments.length === 0
					? "Nothing to change."
					: summarySegments.map((seg, i) => (
							<span key={seg.outcome}>
								{i > 0 ? <span className={styles.passSep}>·</span> : null}
								<button
									type="button"
									className={`${styles.passSeg} ${outcomeFilter === seg.outcome ? styles.passSegActive : ""}`}
									aria-pressed={outcomeFilter === seg.outcome}
									onClick={() => setOutcomeFilter((cur) => (cur === seg.outcome ? null : seg.outcome))}
								>
									{seg.label}
								</button>
							</span>
						))}
			</div>
			{onShowFixResults ? (
				<button type="button" className={styles.passSeg} onClick={onShowFixResults}>
					See results
				</button>
			) : null}
			{onDismissResolveSummary ? (
				<button
					type="button"
					className={styles.passDismiss}
					onClick={onDismissResolveSummary}
					aria-label="Dismiss summary"
				>
					×
				</button>
			) : null}
		</div>
	) : null;

	// The run's audit library (precedent-aware audit). Rendered in every runId
	// branch so it stays visible while the issues list loads or errors; the panel
	// self-hides when the feature is off or the run has no audit library.
	const auditPanel = runId ? (
		<AuditLibraryPanel
			data={auditLib}
			onRefresh={loadAuditLibrary}
			hideWhenNotReady
			allowSetup
			onSetup={handleSetupAuditLibrary}
			settingUp={settingUpAudit}
			setupError={auditSetupError ?? undefined}
			liveBuildMessage={liveBuildMessage}
			liveCurrent={auditBuildPhase?.current ?? null}
			liveTotal={auditBuildPhase?.total ?? null}
			runId={runId}
		/>
	) : null;

	if (!runId) {
		return (
			<div className={styles.root}>
				<div className={styles.empty}>No run yet — start or load a draft to see its issues.</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className={styles.root}>
				{summaryLine}
				{reviewPanel}
				{auditPanel}
				<div className={styles.loading}>
					<Spinner size="extra-tiny" />
					<span>Collecting issues across the submission…</span>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.root}>
				{summaryLine}
				{reviewPanel}
				{auditPanel}
				<div className={styles.error}>Couldn&apos;t load the submission&apos;s issues: {error}</div>
			</div>
		);
	}

	const counts = payload?.status_counts ?? { open: 0, resolved: 0, dismissed: 0, stale: 0, total: 0 };
	const passId = resolveSummary?.pass_id ?? null;
	const allFlatIssues = flattenIssues(payload?.sections ?? []);
	const inPass = (issue: { id: string }) => Boolean(fixingIssueIds?.has(issue.id));
	const isFixing = (issue: { id: string; module_id: string }) =>
		Boolean(fixingModuleIds?.has(issue.module_id)) && (!fixingIssueIds?.size || inPass(issue));
	const liveLineFor = (moduleId: string): string | null => {
		const lines = fixActivityBySection?.get(moduleId);
		return lines && lines.length > 0 ? lines[lines.length - 1].text : null;
	};
	const sectionIssues = allFlatIssues.filter(({ issue }) => inSection(issue));
	const viewIssues = sectionIssues.filter(({ issue }) => inIssuesView(issue, view, liveIssueOutcomes));
	// The server's record of every pass, narrowed to the picked section.
	const sectionPasses =
		sectionFilter === null
			? serverPasses
			: serverPasses
					.map((p) => ({ ...p, flags: (p.flags ?? []).filter((f) => f.module_id === sectionFilter) }))
					.filter((p) => p.flags.length > 0);
	const history = mergeFixHistory(
		sectionPasses,
		sectionIssues.map(({ issue }) => issue),
	);
	const viewCount = (v: IssuesView) =>
		v === "history"
			? history.length
			: sectionIssues.filter(({ issue }) => inIssuesView(issue, v, liveIssueOutcomes)).length;
	const sectionOptions = issueSectionOptions(payload?.sections ?? [], (issue) =>
		view === "history" ? Boolean(issue.last_fix) : inIssuesView(issue, view, liveIssueOutcomes),
	);
	// Grouped by section in document order; within a section the flags of the
	// running pass come first, then the server's order.
	const sectionOrder = new Map((payload?.sections ?? []).map((s, i) => [s.module_id, i]));
	const sectionTitle = new Map(
		(payload?.sections ?? []).map((s) => [s.module_id, [sectionRef(s.number), s.title].filter(Boolean).join(" ")]),
	);
	const filteredIssues = outcomeFilter
		? viewIssues.filter(({ issue }) =>
				outcomeFilter === "proposed"
					? Boolean(pendingByIssue[issue.id]) ||
						effectiveOutcome(issue, liveIssueOutcomes, passId)?.outcome === "proposed"
					: effectiveOutcome(issue, liveIssueOutcomes, passId)?.outcome === outcomeFilter,
			)
		: viewIssues;
	const flatIssues = filteredIssues
		.map((row, i) => ({ row, i }))
		.sort(
			(a, b) =>
				(sectionOrder.get(a.row.issue.module_id) ?? 0) - (sectionOrder.get(b.row.issue.module_id) ?? 0) ||
				Number(inPass(b.row.issue)) - Number(inPass(a.row.issue)) ||
				a.i - b.i,
		)
		.map(({ row }) => row);
	const activeFilterLabel = outcomeFilter
		? summarySegments.find((seg) => seg.outcome === outcomeFilter)?.label ?? null
		: null;

	return (
		<div className={styles.root}>
			{summaryLine}
			{reviewPanel}
			{auditPanel}
			<div className={styles.summary}>
				<div className={styles.titleRow}>
					<h2 className={styles.title}>Issues to review</h2>
					{runId ? (
						<button
							type="button"
							className={styles.cleanCopyBtn}
							disabled={cleanCopy === "working"}
							onClick={makeCleanCopy}
							title="Open a copy of this document without regulator, language and reviewer notes"
							data-testid="issues-clean-copy"
						>
							{cleanCopy === "working" ? "Making a clean copy…" : "Clean copy"}
						</button>
					) : null}
				</div>
				{cleanCopyNote ? <span className={styles.cleanCopyNote}>{cleanCopyNote}</span> : null}
				{/* biome-ignore lint/a11y/useSemanticElements: a tab strip of views, not a form */}
				<div className={styles.viewTabs} role="tablist" aria-label="Issues view">
					{(
						[
							["open", "Open"],
							["tried", "Fix tried"],
							["resolved", "Resolved"],
							["all", "All"],
							["history", "History"],
						] as const
					).map(([v, label]) => (
						<button
							type="button"
							role="tab"
							key={v}
							aria-selected={view === v}
							className={`${styles.viewTab} ${view === v ? styles.viewTabOn : ""}`}
							onClick={() => setView(v)}
							data-testid={`issues-view-${v}`}
						>
							{label}
							<span className={styles.countNum}> {viewCount(v)}</span>
						</button>
					))}
				</div>
				<label className={styles.sectionPick}>
					<span className={styles.typeLead}>Section</span>
					<select
						className={styles.sectionSelect}
						value={sectionFilter ?? ""}
						onChange={(e) => setSectionFilter(e.target.value || null)}
						data-testid="issues-section-filter"
					>
						<option value="">Whole document</option>
						{sectionOptions.map((o) => (
							<option key={o.moduleId} value={o.moduleId}>
								{o.label} ({o.count})
							</option>
						))}
						{sectionFilter && !sectionOptions.some((o) => o.moduleId === sectionFilter) ? (
							<option value={sectionFilter}>{sectionTitle.get(sectionFilter) ?? sectionFilter} (0)</option>
						) : null}
					</select>
				</label>
				{selectionEnabled && allOpenIds.length > 0 ? (
					<div className={styles.summaryRow}>
						<label className={styles.selectAll}>
							<input
								type="checkbox"
								className={styles.checkbox}
								checked={allSelected}
								onChange={toggleAll}
								aria-label={sectionFilter ? "Select all open issues in this section" : "Select all open issues"}
							/>
							{sectionFilter ? "Select all in section" : "Select all"}
						</label>
						<span className={styles.selCount}>{selectedCount} selected</span>
						{onFixNow ? (
							<button
								type="button"
								className={styles.sourcesLink}
								disabled={selectedCount === 0}
								onClick={startResolve}
								title="Pick which source documents to search before fixing"
							>
								Choose sources…
							</button>
						) : null}
						<button
							type="button"
							className={styles.resolveBtn}
							disabled={selectedCount === 0}
							onClick={onFixNow ? fixSelectedNow : startResolve}
							style={onFixNow ? { marginLeft: 8 } : undefined}
							title="Search your sources and propose fixes for the selected flags"
						>
							Fix {selectedCount} flag{selectedCount === 1 ? "" : "s"}
						</button>
					</div>
				) : null}
				{selectionEnabled && openTypes.length > 1 ? (
					// biome-ignore lint/a11y/useSemanticElements: a chip row, not a form fieldset
					<div className={styles.typeRow} role="group" aria-label="Select by flag type">
						<span className={styles.typeLead}>By type</span>
						{openTypes.map((t) => {
							const all = t.ids.every((id) => selectedIds.has(id));
							return (
								<button
									type="button"
									key={t.label}
									className={`${styles.typeChip} ${all ? styles.typeChipOn : ""}`}
									aria-pressed={all}
									title={all ? `Unselect every ${t.label} flag` : `Select every ${t.label} flag`}
									onClick={() => toggleType(t.ids)}
								>
									{t.label} · {t.ids.length}
								</button>
							);
						})}
					</div>
				) : null}
			</div>

			{actionError ? <div className={styles.error}>{actionError}</div> : null}
			{undoDismiss ? (
				<div className={styles.undoRow} role="status">
					<span>Marked N/A.</span>
					<button
						type="button"
						className={styles.sourcesLink}
						style={{ marginLeft: 0 }}
						onClick={() => {
							const issue = undoDismiss;
							setUndoDismiss(null);
							void handlePatch(issue, "open");
						}}
					>
						Undo
					</button>
				</div>
			) : null}

			{outcomeFilter ? (
				<div className={styles.filterNote}>
					<span>
						Showing {flatIssues.length} of {allFlatIssues.length}
						{activeFilterLabel ? ` · ${activeFilterLabel}` : ""}
					</span>
					<button type="button" className={styles.passSeg} onClick={() => setOutcomeFilter(null)}>
						Show all
					</button>
				</div>
			) : null}

			{view === "history" ? (
				history.length === 0 ? (
					<div className={styles.empty}>No fix has been tried on this document yet.</div>
				) : (
					<div className={styles.list} data-testid="fix-history">
						{history.map((rec) => (
							<div key={rec.passId} className={styles.historyRow}>
								<span className={styles.historyWhen}>
									{rec.at ? new Date(rec.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "Earlier"}
								</span>
								<span>{fixPassLine(rec)}</span>
								{rec.by || rec.scope ? (
									<span className={styles.historyWhen}>
										{rec.by ? `${rec.by} · ` : ""}
										{rec.scope && rec.scope.length > 0
											? `searched ${rec.scope.length === 1 ? rec.scope[0] : `${rec.scope.length} documents`}`
											: "searched the whole library"}
									</span>
								) : null}
							</div>
						))}
					</div>
				)
			) : flatIssues.length === 0 && outcomeFilter ? (
				<div className={styles.empty}>No flags match this filter.</div>
			) : flatIssues.length === 0 ? (
				<div className={styles.empty}>
					{view === "open" ? "No open issues. ✓" : view === "tried" ? "No fix has been tried yet." : "Nothing here yet."}
				</div>
			) : (
				<div className={styles.list}>
					{flatIssues.map(({ issue, sectionNumber }, idx) => (
						<Fragment key={issue.id}>
							{idx === 0 || flatIssues[idx - 1].issue.module_id !== issue.module_id ? (
								<div className={styles.sectionHead} data-testid="issues-section-head">
									{sectionTitle.get(issue.module_id) ?? sectionRef(sectionNumber)}
								</div>
							) : null}
						<IssueRow
							issue={issue}
							sectionRef={sectionRef(sectionNumber)}
							onJump={(i) => jumpToMarker(i, sectionNumber)}
							onDismiss={(i) => void handlePatch(i, "dismissed")}
							onReopen={(i) => void handlePatch(i, "open")}
							onResolveWithAsthra={onResolveWithAsthra}
							selectable={selectionEnabled}
							selected={selectedIds.has(issue.id)}
							onToggleSelect={toggleIssue}
							expanded={expandedId === issue.id}
							onToggleExpanded={toggleExpanded}
							fixing={isFixing(issue)}
							liveLine={isFixing(issue) ? liveLineFor(issue.module_id) : null}
							liveOutcome={liveIssueOutcomes?.[issue.id] ?? null}
							pendingProposalId={pendingByIssue[issue.id] ?? null}
							onReviewChange={reviewChange}
							onRetryFix={onFixNow || onResolveSelected ? retryFix : undefined}
							onFixNow={onFixNow ? retryFix : undefined}
							inlineChange={(() => {
								const pid = pendingByIssue[issue.id];
								const item = pid && reviewFlow ? queuedById[pid] : undefined;
								return item && reviewFlow ? (
									<InlineChange
										item={item}
										busy={reviewBusy}
										onApprove={(id) => void reviewFlow.approveById(id)}
										onReject={(id) => void reviewFlow.rejectById(id)}
										onOpenReview={reviewChange}
									/>
								) : undefined;
							})()}
						/>
						</Fragment>
					))}
				</div>
			)}
		</div>
	);
};

export default SubmissionIssuesTab;
